// MUI ライブラリから Stack と Typography コンポーネントをインポート
import { Box, IconButton, Modal, Stack, Typography } from '@mui/material';
// MUI アイコン（CloseIcon）をインポート
import CloseIcon from '@mui/icons-material/Close';
// React から useEffect, useMemo, useState フックをインポート
import { useEffect, useMemo, useState } from 'react';
// react-i18next から useTranslation フックをインポート
import { useTranslation } from 'react-i18next';
// react-router-dom から useLocation フックをインポート
import { useLocation } from 'react-router-dom';
// コンプライアンスアンケートの型定義をインポート
import { QuestionCompliaEnquete } from 'src/@types/compliaEnquete';
// 一般アンケートの型定義（GeneralEnquete, Question）をインポート
import { GeneralEnquete, Question } from 'src/@types/generalEnquete';
// スタイル付きボタンコンポーネントをインポート
import { StyledButton } from 'src/components/app-components/StyledButton';
// エラーツールチップコンポーネントをインポート
import { ErrorTooltip } from 'src/components/app-components/StyledTooltip';
// 定数（ANSWER_STATUS, AnswerStatus）をインポート
import { ANSWER_STATUS, AnswerStatus } from 'src/constants';
// ファイル型定義をインポート
import { FileType } from 'src/features/general/enquete-answer/store/enqueteAnswerSlice';
// 一般質問初期化アクションをインポート
import { setInitGeneralAction5Questions, setInitGeneralQuestions } from 'src/features/general/enquete-answer/store/enqueteGeneralAnswerSlice';
// 回答質問作成ユーティリティをインポート
import { createAnswerQuestions, createTranslationAnswerQuestions } from 'src/features/general/enquete-answer/utils/createAnswerQuestions';
// 認証フックをインポート
import useAuth from 'src/hooks/useAuth';
// ロケールフックをインポート
import useLocales from 'src/hooks/useLocales';
// 権限フックをインポート
import { usePermission } from 'src/hooks/usePermission';
// Redux の dispatch と useAppSelector をインポート
import { dispatch, useAppSelector } from 'src/redux/store';
// アンケート回答保存フックをインポート
import useEnqueteAnswerSave from '../hooks/useEnqueteAnswerSave';
// 差戻しモーダルレイアウトコンポーネントをインポート
import { SwitchReversionModalLayout } from './common/components/SwitchReversionModalLayout';
// 差戻し処理フックをインポート
import { setIsSubmit, setSubmitPopSuccess } from 'src/features/general/enquete-answer/store/enqueteAnswerSlice';
import { useReturnedHandleClick } from './common/hooks/useReturnedHandleClick';
//一問一答提出ポップアップコンポーネントをインポート
import { TAB_VALUE_COMPLIA as TAB_VALUE } from 'src/constants';
//提出時ポップアップ画像
import submitTooltip from 'src/assets/submitTooltip.png';

// アンケート回答保存ボタンのプロパティインターフェースを定義
export interface EnqueteAnswerSaveButtonProps {
  // 回答者ターゲット ID
  respondentTargetId: string;
  // 確認リクエスト ID（null 許容）
  confirmRequestId: string | null;
  // ファイル選択ボックスの開閉状態設定関数
  setIsSelectFileBoxOpen: any;
  // 選択されたファイルのリスト
  selectedFile: FileType[];
  // 削除されたファイルのリスト
  deletedFile: FileType[];
  // 上部表示フラグ
  topFlg: boolean;
  //無効提出完了ポップアップフラグ
  disabledSubmitPop?: boolean
}
// コンプライアンス保存ボタンコンポーネントの定義
export const EnqueteAnswerSpecialQuestionCompliaSaveButtons: React.FC<EnqueteAnswerSaveButtonProps> = (props): React.ReactElement => {
  // 認証ユーザー情報を取得
  const { user } = useAuth();
  // 翻訳関数を取得
  const { t } = useTranslation();
  // 現在の言語設定を取得
  const { currentLang } = useLocales();
  // アンケート回答保存処理フックを使用（回答済みフラグと保存ハンドラー）
  const { isAnswered, compliaSaveHandler, isAnsweredAction5 } = useEnqueteAnswerSave(props.respondentTargetId);
  // 一般回答ステートからエラー状態を取得
  const hasError = useAppSelector((stateGeneral) => stateGeneral.enqueteGeneralAnswer.hasError);
  // 一般回答ステートから質問データを取得
  const questions = useAppSelector((stateGeneral) => stateGeneral.enqueteGeneralAnswer.questions);
  // 一般回答アクション⑤ステートからエラー状態を取得
  const hasAction5Error = useAppSelector((stateGeneral) => stateGeneral.enqueteGeneralAnswerAction5.hasAction5Error);
  // 一般回答アクション⑤ステートから質問アクション⑤データを取得
  const action5Questions = useAppSelector((stateGeneral) => stateGeneral.enqueteGeneralAnswerAction5.questions);
  // 確認リクエストデータを取得
  const confirmRequestData = useAppSelector((state) => state.enqueteAnswer.confirmRequestData);
  // アンケート回答オープン状態を取得
  const isEnqueteAnswerOpen = useAppSelector((state) => state.enqueteAnswer.isEnqueteAnswerOpen);
  // 調査詳細データを取得
  const surveyDetailData = useAppSelector((stateGeneral) => stateGeneral.enqueteAnswer.surveyDetailData);
  // 調査詳細アクション⑤データを取得
  const surveyDetailAction5Data = useAppSelector((stateGeneral) => stateGeneral.enqueteAnswer.surveyDetailAction5Data);
  // アンケート回答データを取得
  const enqueteAnswerData = useAppSelector((state) => state.enqueteAnswer.enqueteAnswerData);
    // アンケート回答アクション⑤データを取得
  const enqueteAnswerAction5Data = useAppSelector((state) => state.enqueteAnswer.enqueteAnswerAction5Data);
  // Redux から現在のタブかどうかを取得
  const currentAnswerTab = useAppSelector((state) => state.enqueteAnswer.currentAnswerTab);
  // モーダルオープン状態のステートを定義
  const [isOpen, setIsOpen] = useState<boolean>(false);

  // 差戻し処理フックを使用（オープン状態設定）
  const { isReversionRequest } = useReturnedHandleClick(setIsOpen);
  // ユーザー ID を設定（ユーザーが存在すれば email、それ以外の場合は空文字とする）
  const userId = null !== user ? user.email : '';
  // 権限フックを使用（各種ロールフラグを取得）
  const { isAdmin, isViewerAdmin, isSurveyOwner, isRespondentPerson, isAnswerEditor, isAnswerReader, isConfirmor } = usePermission();
  // 現在の URL パラメータを取得
  const urlParams = useLocation().search;
  // URL から confirmRequestId パラメータ値を取得
  const confirmRequestIdParamsValue: string | null = new URLSearchParams(urlParams).get('confirmRequestId');

  // 編集可能（提出・一時保存）なユーザーの権限・ロールかを判定
  const canEdit =
    isAdmin || isSurveyOwner || isRespondentPerson || isAnswerEditor || (isConfirmor && ((enqueteAnswerData.status === ANSWER_STATUS.ANSWERED && currentAnswerTab === TAB_VALUE.QUESTION) || (enqueteAnswerAction5Data.status === ANSWER_STATUS.ANSWERED && currentAnswerTab === TAB_VALUE.QUESTION_ACTION5)));
  // 提出不可フラグ（編集権限がない場合）
  const canNotSubmit = !canEdit;
  const closeFlg = ((enqueteAnswerData.status === ANSWER_STATUS.ANSWERED && currentAnswerTab === TAB_VALUE.QUESTION) || (enqueteAnswerAction5Data.status === ANSWER_STATUS.ANSWERED && currentAnswerTab === TAB_VALUE.QUESTION_ACTION5) || (currentAnswerTab !== TAB_VALUE.QUESTION && currentAnswerTab !== TAB_VALUE.QUESTION_ACTION5)) && !isEnqueteAnswerOpen;
  // エラーロールフラグ（閲覧者・回答リーダー・確認者以外）
  const errorRole = !isViewerAdmin && !isAnswerReader && !isConfirmor;
  // アンケートフォームデータをメモ化（タブデータ 0 番目）
  const memolizedEnqueteFormData = useMemo(() => surveyDetailData.surveyFormData.formData.tabData[0], [surveyDetailData]) as QuestionCompliaEnquete;
  // 質問データをメモ化
  const memolizedQuestions = useMemo(() => questions, [questions]) as Question[];
  // アンケートアクション⑤フォームデータをメモ化（タブデータ 0 番目）
  const memolizedEnqueteAction5FormData = useMemo(() => surveyDetailAction5Data?.surveyFormData?.formData?.tabData[0], [surveyDetailAction5Data]) as QuestionCompliaEnquete;
  // 質問アクション⑤データをメモ化
  const memolizedAction5Questions = useMemo(() => action5Questions, [action5Questions]) as Question[];
  // エラーツールチップメッセージを生成（期間外または無効場合）
  const errorToolTipMessage = !isEnqueteAnswerOpen ? t('message.outOfSurveyTerm') : (hasError && currentAnswerTab === TAB_VALUE.QUESTION) || (hasAction5Error && currentAnswerTab === TAB_VALUE.QUESTION_ACTION5) ? t('message.invalid') : '';
  // マージントップ値を条件に基づいて設定
  const marginTopValue = props.confirmRequestId && confirmRequestData.confirmerId === userId ? '20px' : '50px';
  // 一問一答提出ポップアップモーダルオープン状態のステートを定義
  const [isSubmitPopOpen, setIsSubmitPopOpen] = useState<boolean>(false);
  // 一問一答提出ポップアップモーダルフラグ状態のステートを定義 confirmationPop:確認ポップアップ completionPop：完了ポップアップ
  const [isSubmitPopFlag, setIsSubmitPopFlag] = useState<string>();
  // Redux から現在のポップアップのステータスかどうかを取得
  const submitPopSuccess = useAppSelector((state) => state.enqueteAnswer.submitPopSuccess);
  // 一時保存ボタン無効フラグ
  const [isSaveBtnDisable, setIsSaveBtnDisable] = useState<boolean>(false);
  // 提出ボタン無効フラグ
  const [isSubmitBtnDisable, setIsSubmitBtnDisable] = useState<boolean>(false);

  // 一問一答提出ポップアップモーダル閉じる
  const closeSubmitPopModal =  () =>{
    setIsSubmitPopOpen(false);
  }

  //提出時と一時保存の処理分岐
  const submitTabSelect = (answerStatus: AnswerStatus) =>{
    //一問一答場合
    if(currentAnswerTab === TAB_VALUE.QUESTION && answerStatus === ANSWER_STATUS.ANSWERED){
      //ポップアップ起動する
      setIsSubmitPopOpen(true);
      //ポップアップ状態設定
      setIsSubmitPopFlag('confirmationPop');
    //一問一答アクション⑤場合
    }else if(currentAnswerTab === TAB_VALUE.QUESTION_ACTION5){
      submitHandler(ANSWER_STATUS.ANSWERED);
    }

  }

  // 言語変更時に質問データを初期化し、Redux に dispatch する効果フック
  useEffect(() => {
    // 質問データが空の場合は処理をスキップ
    if (!(memolizedQuestions.length === 0 || memolizedEnqueteFormData.questionsJpn?.length === 0)){
      // 現在の言語に基づいて質問データを選択（日本語または英語）
      const enqueteAnswerQuestionsEmptyValue =
        currentLang.value === 'ja' ? memolizedEnqueteFormData.questionsJpn : memolizedEnqueteFormData.questionsEng;
      // 翻訳された回答質問データを作成
      const convetedEnqueteAnswer = createTranslationAnswerQuestions(memolizedQuestions, JSON.parse(JSON.stringify(enqueteAnswerQuestionsEmptyValue)));
      // 一般質問初期化アクションを dispatch
      dispatch(setInitGeneralQuestions(convetedEnqueteAnswer));

    }

    // 質問アクション⑤データが空の場合は処理をスキップ
    if (!(memolizedAction5Questions.length === 0 || memolizedEnqueteAction5FormData.questionsJpn?.length === 0)){
      // 現在の言語に基づいて質問データを選択（日本語または英語）
      const enqueteAnswerQuestionsEmptyValue =
        currentLang.value === 'ja' ? memolizedEnqueteAction5FormData.questionsJpn : memolizedEnqueteAction5FormData.questionsEng;
      // 翻訳された回答質問データを作成
      const convetedEnqueteAnswer = createTranslationAnswerQuestions(memolizedAction5Questions, JSON.parse(JSON.stringify(enqueteAnswerQuestionsEmptyValue)));
      // 一般質問初期化アクションを dispatch
      dispatch(setInitGeneralAction5Questions(convetedEnqueteAnswer));

    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentLang.value, dispatch]);

  //タブの変更に応じて、ボタンの状態を判定する
  useEffect(() => {
    //タブは一問一答場合
    if(currentAnswerTab === TAB_VALUE.QUESTION){
      //一時保存ボタン: 一問一答が回答済またはエラーの場合は非活性
      (enqueteAnswerData.status === ANSWER_STATUS.ANSWERED || isAnswered) ? setIsSaveBtnDisable(true) : setIsSaveBtnDisable(false);
    
      //提出ボタン:一問一答がエラーの場合は非活性
      hasError ? setIsSubmitBtnDisable(true) : setIsSubmitBtnDisable(false);

    //タブは一問一答(アクション⑤)場合
    }else if(currentAnswerTab === TAB_VALUE.QUESTION_ACTION5){
      //一時保存ボタン: 一問一答(アクション⑤)が回答済またはエラーの場合は非活性
      (enqueteAnswerAction5Data.status === ANSWER_STATUS.ANSWERED || !surveyDetailAction5Data?.surveyFormData?.formData?.tabData[0] || isAnsweredAction5) ? setIsSaveBtnDisable(true) : setIsSaveBtnDisable(false);
    
      //提出ボタン:一問一答(アクション⑤)がエラーの場合は非活性
      (hasAction5Error || !surveyDetailAction5Data?.surveyFormData?.formData?.tabData[0]) ? setIsSubmitBtnDisable(true) : setIsSubmitBtnDisable(false);

    //タブは回答確認状況 / 確認依頼場合
    }else if(currentAnswerTab === TAB_VALUE.CONFIRM){
      setIsSaveBtnDisable(true);
      setIsSubmitBtnDisable(true);

    //タブは回答者変更場合
    }else if(currentAnswerTab === TAB_VALUE.CHANGE){
      setIsSaveBtnDisable(true);
      setIsSubmitBtnDisable(true);
    }
  },[currentAnswerTab, hasError, hasAction5Error])

  //提出成功、提出完了ポップアップ
  useEffect(() => {
    //提出成功、提出完了ポップアップ
    if(submitPopSuccess && !props.disabledSubmitPop){
      setIsSubmitPopOpen(true);
      setIsSubmitPopFlag('completionPop');
      dispatch(setSubmitPopSuccess(false));
    }
  },[submitPopSuccess])

  // 提出処理ハンドラー（回答ステータスに応じて保存）
  const submitHandler = async (answerStatus: AnswerStatus) => {
    let success : any;
    try {
      dispatch(setIsSubmit(true));
      // 画面項目の入力処理にdebounceを使用しているため、保存または提出時に1秒待機し、入力内容が正しく更新されることを保証する
      await new Promise((resolve) => setTimeout(resolve, 1000));
      const currentSurveyDetailData = currentAnswerTab === TAB_VALUE.QUESTION_ACTION5 ? surveyDetailAction5Data : surveyDetailData;
      // 現在の言語とデータに基づいて回答質問を作成
      const answerQuestions = createAnswerQuestions(currentLang.value, currentSurveyDetailData, currentAnswerTab === TAB_VALUE.QUESTION_ACTION5);
      // アンケートフォーム回答データを構築
      const surveyFormAnswerData = {
        // 日本語質問データ
        questionsJpn: answerQuestions.jpn,
        // 英語質問データ
        questionsEng: answerQuestions.eng,
      } as GeneralEnquete;
      // コンプライアンス保存ハンドラーを呼び出し（ファイル情報を含む）
      success = await compliaSaveHandler(surveyFormAnswerData, answerStatus, props.selectedFile ?? [], props.deletedFile ?? [], currentSurveyDetailData?.id);

    } finally {
      dispatch(setIsSubmit(false));
      if(answerStatus === ANSWER_STATUS.ANSWERED){
        dispatch(setSubmitPopSuccess(success));
      }
    }
  };

  // ファイルチェックボックスオープン処理ハンドラー
  const openFileCheckBoxHandler = async () => {
    // // 閲覧者または回答リーダーの場合は処理を中断
    // if (isViewerAdmin || isAnswerReader) {
    //   return;
    // }
    // ファイル選択ボックスオープン処理を実行
    handleSelectFileBoxOpen();
  };

  // ファイル選択ボックスオープン状態を設定する関数
  const handleSelectFileBoxOpen = () => props.setIsSelectFileBoxOpen(true);

  // コンポーネントの JSX レンダリング開始
  return (
    // フラグメントで複数の要素をラップ
    <>
      {/* // レスポンシブ対応の Stack レイアウト（縦方向または横方向） */}
      <Stack
        // 画面サイズに応じた方向設定（xs は列、sm 以上は行）
        direction={{ xs: 'column', sm: 'row' }}
        // 画面サイズに応じたスペーシング設定
        spacing={{ xs: 1, sm: 2, md: 4 }}
        // 高さおよびマージントップのスタイル設定（条件付き）
        sx={{ height: '22px', marginTop: marginTopValue === '50px' && !confirmRequestIdParamsValue ? '35px' : props.topFlg ? '5px' : '35px' }}>
        {/* // 選択されたファイルがある場合のみ表示 */}
        {props.selectedFile && props.selectedFile.length > 0 && (
          // ファイル表示用の Stack
          <Stack>
            {/* // ファイル添付メッセージを表示する Typography */}
            <Typography variant="body2" color="text.secondary">
              {/* // 翻訳されたファイル添付ラベルメッセージ */}
              {t('enqueteAnswerInput.modal.fileAttachment.label.promptMessage')}
            </Typography>
          </Stack>
        )}
      </Stack>
      {/* // ボタン配置用の Stack レイアウト（レスポンシブ対応） */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 1, sm: 2, md: 4 }}>
        {/* <Stack direction="column" spacing={1} alignItems="flex-start"> */}
        {/* // コメントアウトされたレイアウトコード // ファイル添付ボタン（クリックでファイル選択ボックスを開く） */}
        <StyledButton onClick={() => openFileCheckBoxHandler()} >{t('button.attachedFile')}</StyledButton>
        {/* </Stack> */}
        {/* // コメントアウトされた閉じタグ // 一時保存ボタン（回答済みまたは編集権限がない場合は無効） */}
        <StyledButton
          // クリック時に提出ハンドラーを呼び出し（戻された場合は戻された状態、それ以外の場合は保存状態とする）
          onClick={() => submitHandler(enqueteAnswerData.status === ANSWER_STATUS.RETURNED ? ANSWER_STATUS.RETURNED : ANSWER_STATUS.SAVED)}
          // 回答済みまたは編集不可の場合は無効化
          isDisabled={!canEdit || isSaveBtnDisable}>
          {/* // 一時保存ボタンのラベル */}
          {t('button.temporarilySave')}
        </StyledButton>
        {/* // エラーツールチップコンポーネント（無効状態やエラー時にメッセージを表示） */}
        <ErrorTooltip
          // ツールチップのタイトル（エラーメッセージ）
          title={errorToolTipMessage}
          // ツールチップ表示条件（権限エラーかつ提出可能かつ期間外またはエラーあり）
          open={errorRole && !canNotSubmit && (!isEnqueteAnswerOpen || (hasError && currentAnswerTab === TAB_VALUE.QUESTION) || (hasAction5Error && currentAnswerTab === TAB_VALUE.QUESTION_ACTION5)) && !closeFlg}
          // ツールチップの配置位置（上部）
          placement="top"
          // ツールチップの色（secondary）
          color="secondary"
          // 矢印表示フラグ
          arrow>
          {/* // ツールチップでラップする span 要素 */}
          <span>
            {/* // 提出ボタン（提出不可または期間外またはエラーがある場合は無効） */}
            <StyledButton onClick={() => submitTabSelect(ANSWER_STATUS.ANSWERED)} isDisabled={canNotSubmit || !isEnqueteAnswerOpen || isSubmitBtnDisable}>
              {/* // 提出ボタンのラベル */}
              {t('button.submit')}
            </StyledButton>
          </span>
        </ErrorTooltip>
      </Stack>
      {/* // 差戻しモーダルレイアウトコンポーネント（オープン状態とリクエストフラグを渡す） */}
      <SwitchReversionModalLayout isOpen={isOpen} setIsOpen={setIsOpen} isRequest={isReversionRequest} />
      {/* 一問一答提出ポップアップ*/}
      {isSubmitPopFlag === 'confirmationPop' && 
        <Modal open={isSubmitPopOpen} onClose={closeSubmitPopModal} >
          {/* // Box コンポーネント（絶対配置、中央揃え） */}
          <Box
            // div 要素としてレンダリング
            component={'div'}
            // スタイル設定（位置、サイズ、背景など）
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '70vw',
              height: '50vh',
              bgcolor: 'background.paper',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: 24,
              p: 3,
          }}>
            {/* クローズボタン（IconButton） */}
            <IconButton
              // aria-label 属性
              aria-label="close"
              // クリックイベント（キャンセル）
              onClick={closeSubmitPopModal}
              // スタイル設定（位置、色）
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                color: (theme) => theme.palette.grey[500],
              }}>
              {/* 閉じるアイコン */}
              <CloseIcon />
            </IconButton>
            {/* 提出時ポップアップ */}
            <Box>
              <Typography variant="body2"  sx={{whiteSpace: 'pre-line', color : 'black'}}>
                {/* 提出時ポップアップメッセージ */}
                {`${t('enqueteAnswerInput.modal.submitPopup.submitPopupMsg')}`}
              </Typography>
            </Box>
            {/*提出時ポップアップ画像*/}
            <Box 
              component="img"
              sx={{ flex: 1, overflow: 'auto' }} 
              src={submitTooltip}
             >             
            </Box>
            {/* 提出ボタン（StyledButton） */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 1,
                mt: 2,
              }}
            >
              <StyledButton sx={{ width: 90 }} onClick={() => submitHandler(ANSWER_STATUS.ANSWERED)} >
                {/* 提出された登録ラベル */}
                {t('button.submit')}
              </StyledButton>
            </Box>
          </Box>
       </Modal>}
       {isSubmitPopFlag === 'completionPop' && 
        <Modal open={isSubmitPopOpen} onClose={closeSubmitPopModal} disableScrollLock={true}>
          <Box
            // div 要素としてレンダリング
            component={'div'}
            // スタイル設定（位置、サイズ、背景など）
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '800px',
              height: '100px',
              bgcolor: 'background.paper',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: 24,
              p: 3,
              borderRadius: 3,
            }}>
            {/* クローズボタン（IconButton） */}
            <IconButton
              // aria-label 属性
              aria-label="close"
              // クリックイベント（キャンセル）
              onClick={closeSubmitPopModal}
              // スタイル設定（位置、色）
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                color: (theme) => theme.palette.grey[500],
              }}>
              {/* 閉じるアイコン */}
              <CloseIcon />
            </IconButton>
            {/* 提出完了ポップアップ */}
            <Typography variant="body2" sx={{whiteSpace: 'pre-line', color : 'black'}}>
              {/* 提出完了ポップアップメッセージ */}
              {`${t('enqueteAnswerInput.modal.submitPopup.submitCompleteMsg')}`}
            </Typography>
          </Box>
        </Modal>}
    </>
  );
};
